/**
 * A finished game, as the browser sends it to be recorded, and the server's check of it.
 *
 * The browser sends only the facts of each hand: who dealt, every seat's final five cards, who
 * knocked and when, and the state the winner took. The server works everything else out again
 * with the game's own rules — hand categories, finishing places, points, contig bonuses, totals
 * and final places — and refuses a game that doesn't add up. Scores are never taken from the
 * browser.
 */
import type { Card, Rank, Suit } from "../engine/cards.ts";
import { evaluate, compareHands, type HandCategory } from "../engine/handEval.ts";
import { scoreHand, type HandScoreBreakdown } from "../engine/scoring.ts";
import { STATE_CODES } from "../engine/states.ts";
import { PEEK_COST, analysePeek, type PeekAnalysis } from "../engine/peek.ts";
import type { TableState } from "../engine/table.ts";

export const MATCH_HANDS = 50;

/** One hand: seat order 0–4 throughout. Cards are codes like "AS", "TD", "7H". */
export interface SubmitHand {
  hand: number;
  dealer: number;
  state: string;
  winner: number;
  /** Seat that knocked, or -1. */
  knocker: number;
  /** The knocker's turn number when they knocked (1 = a quock), or 0. */
  knockRound: number;
  cards: string[][];
}

export interface SubmitSeat {
  name: string;
  party: string;
  colour: string;
  /** null for your own seat; the AI's personality (e.g. "observant") for the others. */
  ai: string | null;
}

/** A peek bought after a hand (PEEK_COST points). The card is the one that was on top of the bones. */
export interface SubmitPeek { hand: number; seat: number; card: string }

export interface SubmitMatch {
  /** Made by the browser when the game starts; sending the same game twice records it once. */
  clientId: string;
  stateMethod: "CHOOSE" | "RANDOM";
  startedAt: number;
  completedAt: number;
  seats: SubmitSeat[];
  hands: SubmitHand[];
  /** Peeks bought (games recorded before peeks existed have none). */
  peeks?: SubmitPeek[];
  /** The capital the President (the winner) named: one of their states. null/missing for older games. */
  capital?: string | null;
}

/** A finished multiplayer game, as the table (src/engine/table.ts) holds it, in the form checkMatch takes. */
export function tableSubmission(g: TableState, id: string, startedAt: number, completedAt: number): SubmitMatch {
  return {
    clientId: id, stateMethod: g.stateMethod, startedAt, completedAt,
    seats: g.players.map((p) => ({ name: p.name, party: p.party, colour: p.colour, ai: p.isHuman ? null : "simple" })),
    hands: g.history.map((r) => ({
      hand: r.handNumber, dealer: r.dealerSeat, state: r.targetState, winner: r.winnerSeat, knocker: r.knockerSeat,
      knockRound: r.knockerSeat < 0 ? 0 : r.knockRound,
      cards: [0, 1, 2, 3, 4].map((seat) => r.results.find((x) => x.seat === seat)!.cards.map(encodeCard)),
    })),
    peeks: (g.peeks ?? []).map((p) => ({ hand: p.hand, seat: p.seat, card: encodeCard(p.card) })),
    capital: g.capital ?? null,
  };
}

const RANK_CODE: Record<number, string> = { 10: "T", 11: "J", 12: "Q", 13: "K", 14: "A" };
const CODE_RANK: Record<string, number> = { T: 10, J: 11, Q: 12, K: 13, A: 14 };

export function encodeCard(c: Card): string {
  return `${RANK_CODE[c.rank] ?? String(c.rank)}${c.suit}`;
}
export function decodeCard(s: string): Card | null {
  const m = /^([2-9TJQKA])([CDHS])$/.exec(s);
  if (!m) return null;
  return { rank: (CODE_RANK[m[1]] ?? Number(m[1])) as Rank, suit: m[2] as Suit };
}

export interface CheckedSeatResult {
  seat: number;
  cards: string;
  category: HandCategory;
  place: number;
  breakdown: HandScoreBreakdown;
}
export interface CheckedHand {
  hand: number;
  dealer: number;
  state: string;
  winner: number;
  knocker: number;
  knockRound: number;
  winningCards: string;
  category: HandCategory;
  contig: boolean;
  results: CheckedSeatResult[];
}
export interface CheckedPeek { hand: number; seat: number; card: string; analysis: PeekAnalysis }
export interface CheckedMatch {
  hands: CheckedHand[];
  /** Per seat: final score (after peeks), states won, final place, points spent on peeks, and the place
   *  they'd have finished without anyone's peeks (the same tie-breaks). */
  totals: { score: number; states: number; place: number; peekPoints: number; placeWithoutPeeks: number }[];
  peeks: CheckedPeek[];
  /** The President (the winner's seat) and the capital they named (null: none sent, or no state to name). */
  president: number;
  capital: string | null;
}

const NAME_MAX = 24;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Re-score a submitted game from its cards. Returns the reason it can't be recorded, or the result. */
/** `table`: a multiplayer game run by the server — any number of people (each `ai: null`), and each of them may peek. */
export function checkMatch(m: SubmitMatch, now = Date.now(), opts: { table?: boolean } = {}): { ok: true; match: CheckedMatch } | { ok: false; reason: string } {
  const bad = (reason: string) => ({ ok: false as const, reason });
  if (!m || typeof m !== "object") return bad("No game sent.");
  if (typeof m.clientId !== "string" || !UUID.test(m.clientId)) return bad("Bad game id.");
  if (m.stateMethod !== "CHOOSE" && m.stateMethod !== "RANDOM") return bad("Unknown state method.");
  if (!Number.isFinite(m.startedAt) || !Number.isFinite(m.completedAt) || m.startedAt > m.completedAt || m.completedAt > now + 10 * 60_000)
    return bad("The game's start and finish times don't make sense.");
  if (!Array.isArray(m.seats) || m.seats.length !== 5) return bad("A game has exactly five seats.");
  const humans = m.seats.filter((s) => s && s.ai === null).length;
  if (opts.table ? humans < 1 : humans !== 1) return bad(opts.table ? "A table game needs at least one person." : "Exactly one seat must be yours.");
  for (const s of m.seats) {
    if (!s || typeof s.name !== "string" || !s.name.trim() || s.name.length > NAME_MAX) return bad("A player's name is missing or too long.");
    if (typeof s.party !== "string" || s.party.length > 40 || typeof s.colour !== "string" || !/^#[0-9a-f]{6}$/i.test(s.colour)) return bad("A player's party or colour is invalid.");
    if (s.ai !== null && (typeof s.ai !== "string" || s.ai.length > 40)) return bad("An AI seat is invalid.");
  }
  if (!Array.isArray(m.hands) || m.hands.length !== MATCH_HANDS) return bad(`A finished game has ${MATCH_HANDS} hands.`);

  // Peeks: only people's seats (the AI never peeks), at most one per seat a hand, each a real card.
  const peeksIn = m.peeks ?? [];
  if (!Array.isArray(peeksIn) || peeksIn.length > MATCH_HANDS * humans) return bad("The peeks don't make sense.");
  const peekAt = new Map<number, SubmitPeek[]>();
  for (const p of peeksIn) {
    if (!p || !Number.isInteger(p.seat) || m.seats[p.seat]?.ai !== null || !Number.isInteger(p.hand) || p.hand < 1 || p.hand > MATCH_HANDS
      || peekAt.get(p.hand)?.some((x) => x.seat === p.seat) || !decodeCard(p.card)) return bad("A peek isn't valid.");
    peekAt.set(p.hand, [...(peekAt.get(p.hand) ?? []), p]);
  }
  const owned: string[][] = [[], [], [], [], []];
  const score = [0, 0, 0, 0, 0], spent = [0, 0, 0, 0, 0];
  const peeks: CheckedPeek[] = [];
  const used = new Set<string>();
  const hands: CheckedHand[] = [];
  for (let i = 0; i < MATCH_HANDS; i++) {
    const h = m.hands[i];
    const n = i + 1;
    if (!h || h.hand !== n) return bad(`Hand ${n} is missing or out of order.`);
    if (!Number.isInteger(h.dealer) || h.dealer < 0 || h.dealer > 4) return bad(`Hand ${n}: bad dealer.`);
    if (i > 0 && h.dealer !== (m.hands[0].dealer + i) % 5) return bad(`Hand ${n}: the deal didn't pass to the left.`);
    if (!STATE_CODES.includes(h.state) || used.has(h.state)) return bad(`Hand ${n}: state ${h.state} is unknown or already taken.`);
    if (!Number.isInteger(h.knocker) || h.knocker < -1 || h.knocker > 4) return bad(`Hand ${n}: bad knocker.`);
    if (h.knocker === -1 ? h.knockRound !== 0 : !(Number.isInteger(h.knockRound) && h.knockRound >= 1 && h.knockRound <= 80))
      return bad(`Hand ${n}: bad knock round.`);
    if (!Array.isArray(h.cards) || h.cards.length !== 5) return bad(`Hand ${n}: every seat needs five cards.`);
    const seen = new Set<string>();
    const seatCards: Card[][] = [];
    for (const codes of h.cards) {
      if (!Array.isArray(codes) || codes.length !== 5) return bad(`Hand ${n}: every seat needs five cards.`);
      const cards: Card[] = [];
      for (const code of codes) {
        const c = decodeCard(code);
        if (!c || seen.has(code)) return bad(`Hand ${n}: a card is invalid or appears twice.`);
        seen.add(code); cards.push(c);
      }
      seatCards.push(cards);
    }
    // Rank the five hands exactly as the game does (no ties: suits break them).
    const ranked = seatCards.map((cards, seat) => ({ seat, rank: evaluate(cards) })).sort((a, b) => compareHands(b.rank, a.rank));
    const winner = ranked[0].seat;
    if (winner !== h.winner) return bad(`Hand ${n}: the cards say seat ${winner + 1} won, not seat ${h.winner + 1}.`);
    const ownedBefore = [...owned[winner]];
    const results: CheckedSeatResult[] = ranked.map((r, k) => ({
      seat: r.seat,
      cards: h.cards[r.seat].join(" "),
      category: r.rank.category,
      place: k + 1,
      breakdown: scoreHand({ position: k + 1, category: r.rank.category, isWinner: r.seat === winner, wonState: r.seat === winner ? h.state : undefined, ownedBefore }),
    }));
    for (const r of results) score[r.seat] += r.breakdown.total;
    owned[winner].push(h.state); used.add(h.state);
    for (const pk of [...(peekAt.get(n) ?? [])].sort((a, b) => a.seat - b.seat)) {
      // (it can be a card someone else ended up holding, but never one of yours: you drew nothing after it)
      const card = decodeCard(pk.card)!;
      if (seatCards[pk.seat].some((c) => c.rank === card.rank && c.suit === card.suit)) return bad(`Hand ${n}: that peek is one of your own cards.`);
      score[pk.seat] -= PEEK_COST; spent[pk.seat] += PEEK_COST;
      peeks.push({ hand: n, seat: pk.seat, card: pk.card, analysis: analysePeek(seatCards[pk.seat], card, seatCards[winner], pk.seat === winner) });
    }
    const win = results[0];
    hands.push({ hand: n, dealer: h.dealer, state: h.state, winner, knocker: h.knocker, knockRound: h.knockRound,
      winningCards: win.cards, category: win.category, contig: win.breakdown.contig > 0, results: results.sort((a, b) => a.seat - b.seat) });
  }
  // Final places: most points, then most states (as the game's standings).
  const rank = (pts: number[]) => [0, 1, 2, 3, 4].sort((a, b) => pts[b] - pts[a] || owned[b].length - owned[a].length || a - b);
  const order = rank(score), without = rank(score.map((x, i) => x + spent[i]));
  const totals = [0, 1, 2, 3, 4].map((seat) => ({ score: score[seat], states: owned[seat].length, place: order.indexOf(seat) + 1,
    peekPoints: spent[seat], placeWithoutPeeks: without.indexOf(seat) + 1 }));
  // The President is the winner; their capital must be one of their own states.
  const president = order[0];
  const capital = m.capital ?? null;
  if (capital !== null && (typeof capital !== "string" || !owned[president].includes(capital))) return bad("The capital must be one of the President's states.");
  return { ok: true, match: { hands, totals, peeks, president, capital } };
}
